/**
 * Text Hygiene Utilities
 * Safe display transformations for raw field values
 * DO NOT use for logic/comparisons - display only
 */

// Raw values that should be hidden or transformed
const RAW_VALUES_TO_HIDE = ['unknown', 'other', 'uncategorized', 'none', 'null', undefined, null, ''];

// Goal label mapping (snake_case → human-friendly)
// This is the SINGLE SOURCE OF TRUTH for goal display labels
export const GOAL_LABELS = {
  // Primary goals with underscores
  'fat_loss': 'Fat Loss',
  'fat_loss_tone': 'Fat Loss + Toning',
  'fat_loss_muscle': 'Fat Loss + Muscle Build',
  'muscle_gain': 'Build Muscle',
  'muscle_build': 'Build Muscle',
  'weight_loss': 'Weight Loss',
  'build_strength': 'Build Strength',
  'general_fitness': 'General Fitness',
  
  // Single word goals
  'muscle': 'Build Muscle',
  'strength': 'Build Strength',
  'general': 'General Fitness',
  'endurance': 'Endurance',
  'flexibility': 'Flexibility',
  
  // Typos and variations (case-insensitive matching applied)
  'weightloss_strenth': 'Weight Loss + Strength',
  'weightloss_strength': 'Weight Loss + Strength',
  'fat loss_muscle': 'Fat Loss + Muscle Build',
  'fat loss muscle': 'Fat Loss + Muscle Build',
  'fatloss_muscle': 'Fat Loss + Muscle Build',
  'fat_loss muscle': 'Fat Loss + Muscle Build',
};

// Muscle group label mapping
export const MUSCLE_LABELS = {
  'quads': 'Quadriceps',
  'hams': 'Hamstrings',
  'hamstrings': 'Hamstrings',
  'glutes': 'Glutes',
  'core': 'Core',
  'abs': 'Abs',
  'chest': 'Chest',
  'back': 'Back',
  'lats': 'Lats',
  'shoulders': 'Shoulders',
  'biceps': 'Biceps',
  'triceps': 'Triceps',
  'forearms': 'Forearms',
  'calves': 'Calves',
  'rear delts': 'Rear Delts',
  'front delts': 'Front Delts',
  'side delts': 'Side Delts',
  'rotator_cuff': 'Rotator Cuff',
  'obliques': 'Obliques',
  'neck': 'Neck',
  'olympic': 'Olympic',
  'full_body': 'Full Body',
  'quadriceps': 'Quadriceps',
};

// Exercise type label mapping
export const EXERCISE_TYPE_LABELS = {
  'strength': 'Strength',
  'plyo': 'Plyometric',
  'stretch': 'Stretch',
  'mobility': 'Mobility',
  'cardio': 'Cardio',
  'cardio_warmup': 'Cardio Warmup',
};

// Movement pattern label mapping
export const MOVEMENT_PATTERN_LABELS = {
  'push': 'Push',
  'pull': 'Pull',
  'squat': 'Squat',
  'hinge': 'Hinge',
  'lunge': 'Lunge',
  'rotation': 'Rotation',
  'carry': 'Carry',
  'core': 'Core',
  'isolation': 'Isolation',
  'compound': 'Compound',
};

/**
 * Check if a value is a "raw" value that shouldn't be displayed
 */
export function isRawValue(value) {
  if (value === undefined || value === null) return true;
  if (typeof value !== 'string') return false;
  const lower = value.toLowerCase().trim();
  return RAW_VALUES_TO_HIDE.includes(lower);
}

/**
 * Format a goal value for display - SINGLE SOURCE OF TRUTH
 * Handles underscores, mixed cases, typos, and variations
 * @param {string} goal - Raw goal value (e.g., "fat_loss", "fat loss_muscle")
 * @returns {string} Human-friendly label (never contains underscores)
 */
export function formatGoal(goal) {
  if (!goal || isRawValue(goal)) return '';
  
  // Normalize: lowercase and trim
  const normalized = goal.toLowerCase().trim();
  
  // Check direct mapping first
  if (GOAL_LABELS[normalized]) {
    return GOAL_LABELS[normalized];
  }
  
  // Check original case mapping (for edge cases)
  if (GOAL_LABELS[goal]) {
    return GOAL_LABELS[goal];
  }
  
  // Check with all underscores converted to spaces
  const withSpaces = normalized.replace(/_/g, ' ');
  if (GOAL_LABELS[withSpaces]) {
    return GOAL_LABELS[withSpaces];
  }
  
  // Check with all spaces converted to underscores
  const withUnderscores = normalized.replace(/\s+/g, '_');
  if (GOAL_LABELS[withUnderscores]) {
    return GOAL_LABELS[withUnderscores];
  }
  
  // Fallback: Replace underscores with spaces and apply Title Case
  // This ensures NO underscores are ever displayed
  return goal
    .replace(/_/g, ' ')           // Replace all underscores with spaces
    .replace(/\s+/g, ' ')         // Collapse multiple spaces
    .trim()                       // Trim whitespace
    .replace(/\b\w/g, c => c.toUpperCase()); // Title Case
}

/**
 * Format a muscle group value for display
 * @param {string} muscle - Raw muscle value
 * @param {string} fallback - Fallback if raw/unknown (default: hidden)
 * @returns {string} Human-friendly label or fallback
 */
export function formatMuscle(muscle, fallback = '') {
  if (!muscle || isRawValue(muscle)) return fallback;
  const lower = muscle.toLowerCase().trim();
  return MUSCLE_LABELS[lower] || muscle.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Format an exercise type for display
 * @param {string} type - Raw exercise type
 * @param {string} fallback - Fallback if raw/unknown
 * @returns {string} Human-friendly label or fallback
 */
export function formatExerciseType(type, fallback = '') {
  if (!type || isRawValue(type)) return fallback;
  const lower = type.toLowerCase().trim();
  return EXERCISE_TYPE_LABELS[lower] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Format a movement pattern for display
 * @param {string} pattern - Raw movement pattern
 * @param {string} fallback - Fallback if raw/unknown
 * @returns {string} Human-friendly label or fallback
 */
export function formatMovementPattern(pattern, fallback = '') {
  if (!pattern || isRawValue(pattern)) return fallback;
  const lower = pattern.toLowerCase().trim();
  return MOVEMENT_PATTERN_LABELS[lower] || pattern.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Format a movement pattern for display, using the whole exercise for context.
 * Calf exercises are stored with movement_pattern="squat" (they ride the squat
 * slot in generation), but should DISPLAY as "Calf Raise". This is display-only
 * and does not change stored data or generation behaviour.
 * @param {object} exercise - exercise object with movement_pattern, primary_muscle
 * @param {string} fallback
 * @returns {string}
 */
export function formatMovementPatternForExercise(exercise, fallback = '') {
  if (!exercise) return fallback;
  const pattern = (exercise.movement_pattern || '').toLowerCase().trim();
  const muscle = (exercise.primary_muscle || '').toLowerCase().trim();
  // Display-only override: calf exercises are stored as "squat" but shown as "Calf Raise"
  if (pattern === 'squat' && muscle === 'calves') {
    return 'Calf Raise';
  }
  return formatMovementPattern(exercise.movement_pattern, fallback);
}

/**
 * Format a difficulty level for display
 * @param {string} difficulty - Raw difficulty
 * @returns {string} Human-friendly label
 */
export function formatDifficulty(difficulty) {
  if (!difficulty || isRawValue(difficulty)) return '';
  return difficulty.charAt(0).toUpperCase() + difficulty.slice(1).toLowerCase();
}

/**
 * Clean an exercise name (remove gender suffixes, trim, etc.)
 * This is for DISPLAY ONLY - do not use for data mutations
 * Safety net for any names that slip through DB cleanup
 * @param {string} name - Raw exercise name
 * @returns {string} Cleaned name
 */
export function cleanExerciseName(name) {
  if (!name) return '';
  // Remove gender tags ANYWHERE in the name (case-insensitive)
  // e.g., "Air Bike (male) Waist" → "Air Bike Waist"
  let cleaned = name.replace(/\s*\((male|female)\)\s*/gi, ' ');
  // Collapse double/multiple spaces
  cleaned = cleaned.replace(/\s{2,}/g, ' ');
  // Trim
  cleaned = cleaned.trim();
  return cleaned;
}

/**
 * Get a safe display value for any field
 * Returns empty string for raw/unknown values
 * @param {string} value - Raw value
 * @param {string} fallback - Optional fallback value
 * @returns {string} Safe display value
 */
export function safeDisplay(value, fallback = '') {
  if (isRawValue(value)) return fallback;
  return value;
}
